import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ImmediateDigest from "./Widgets/ImmediateDigest";
import NewsAnchorWidget from "./Widgets/NewsAnchorWidget";
import { getFeed } from "../../../api/feed";
import { useRightSidebar } from "../../contexts/RightSidebarContext";
import ArticleDetailPanel from "../../Universal/ArticleCardDetailPanel";
import CVEGraph from "../../Universal/CVEGraph";

const IS_CP = process.env.REACT_APP_IS_CP === "true";

/* ─── Theme ─── */
const getTheme = (dark) =>
  dark
    ? {
        cardBg: "rgba(15,23,42,0.60)",
        cardBorder: "rgba(14,165,233,0.12)",
        textPrimary: "rgba(226,232,240,0.9)",
        textSecondary: "rgba(148,163,184,0.7)",
        textMuted: "rgba(148,163,184,0.4)",
        activeText: "rgba(14,165,233,0.9)",
        activeBorder: "rgba(14,165,233,0.30)",
        activeBg: "rgba(14,165,233,0.15)",
        dangerText: "rgba(248,113,113,0.9)",
      }
    : {
        cardBg: "rgba(241,245,249,0.8)",
        cardBorder: "rgba(203,213,225,0.5)",
        textPrimary: "rgba(15,23,42,0.9)",
        textSecondary: "rgba(71,85,105,0.8)",
        textMuted: "rgba(148,163,184,0.6)",
        activeText: "rgba(2,132,199,1)",
        activeBorder: "rgba(14,165,233,0.30)",
        activeBg: "rgba(14,165,233,0.10)",
        dangerText: "rgba(220,38,38,0.9)",
      };

const panelVariants = {
  hidden: { opacity: 0, filter: "blur(6px)" },
  visible: (i) => ({
    opacity: 1,
    filter: "blur(0px)",
    transition: {
      duration: 0.4,
      delay: i * 0.1,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  }),
};

/* ─── Hardcoded payload ─── */
const FEED_PAYLOAD = {
  offset: 0,
  group: "public",
  target_types: [
    "court_cases",
    "neighbors",
    "press_release",
    "gov_award",
    "financial_crawl",
    "edu_crawl",
    "investigative_doc",
    "company_subsidiary",
    "domain_crawl",
    "hunt",
    "apt_news",
    "web_citation",
  ],
  sort: -1,
};

/* ─── Helpers ─── */
const relativeTime = (unix) => {
  if (!unix) return "";
  const diff = Math.floor(Date.now() / 1000) - unix;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

/* ─── Post Card ─── */
const PostCard = ({ article, dark, t, onClick, isSelected }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  return (
    <div
      onClick={() => {
        onClick?.(article);
      }}
      className={[
        "mb-2.5 cursor-pointer overflow-hidden rounded-lg transition-all duration-150",
        "border",
        isSelected
          ? "border-velBlue/60 ring-2 ring-velBlue/40 shadow-login-panel"
          : "border-inputBorder hover:border-surfaceBorder",
      ].join(" ")}
      style={{
        background: dark ? "rgba(15,23,42,0.50)" : "rgba(255,255,255,0.70)",
      }}
    >
      {/* Screenshot — portrait aspect */}
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: "9 / 14" }}
      >
        {article.screenshot && !imgError ? (
          <>
            {!imgLoaded && (
              <div
                className="absolute inset-0 animate-pulse"
                style={{
                  background: dark
                    ? "rgba(30,41,59,0.8)"
                    : "rgba(226,232,240,0.6)",
                }}
              />
            )}
            <img
              src={article.screenshot}
              alt=""
              loading="lazy"
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              className="h-full w-full object-cover object-top"
              style={{ opacity: imgLoaded ? 1 : 0, transition: "opacity 0.3s" }}
            />
          </>
        ) : (
          <div
            className="flex h-full w-full items-center justify-center"
            style={{
              background: dark ? "rgba(30,41,59,0.6)" : "rgba(226,232,240,0.5)",
            }}
          >
            <span className="text-[10px] opacity-30">No preview</span>
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="px-2.5 pb-2.5 pt-2">
        <div className="mb-1 flex items-center gap-1.5">
          <span
            className="truncate text-[10px] font-medium tracking-wide"
            style={{ color: t.activeText }}
          >
            {article.source}
          </span>
          <span className="text-[9px]" style={{ color: t.textSecondary }}>
            ·
          </span>
          <span
            className="shrink-0 text-[9px]"
            style={{ color: t.textSecondary }}
          >
            {article.date ? relativeTime(article.date) : article.timestamp}
          </span>
        </div>
        <div
          className="mb-1.5 line-clamp-2 text-[11px] font-medium leading-snug"
          style={{ color: t.textPrimary }}
        >
          {article.title}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {article.target_type && (
            <span
              className="inline-block rounded-full px-2 py-0.5 text-[9px] font-medium"
              style={{
                background: t.activeBg,
                color: t.activeText,
                border: `1px solid ${t.activeBorder}`,
              }}
            >
              {article.target_type.replace(/_/g, " ")}
            </span>
          )}
          {article.target && (
            <span
              className="inline-block truncate rounded-full px-2 py-0.5 text-[9px]"
              style={{ color: t.textSecondary }}
            >
              {article.target}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Auto-scrolling column (per-column speed, accepts manual nudge) ─── */
const EASE_DURATION = 600;

const ScrollColumn = forwardRef(
  ({ cards, speed, dark, t, isPaused, onCardClick, selectedId }, ref) => {
    const colRef = useRef(null);
    const offsetRef = useRef(0);
    const rafRef = useRef(null);
    const lastTimeRef = useRef(null);
    const velocityRef = useRef(1);
    const targetRef = useRef(1);

    // Expose nudge() so PostWall can push manual scroll deltas into this column
    useImperativeHandle(ref, () => ({
      nudge: (delta) => {
        offsetRef.current += delta;
        const el = colRef.current;
        if (el) {
          const scrollH = el.scrollHeight / 2;
          if (scrollH > 0) {
            while (offsetRef.current >= scrollH) offsetRef.current -= scrollH;
            while (offsetRef.current < 0) offsetRef.current += scrollH;
          }
          el.style.transform = `translateY(-${offsetRef.current}px)`;
        }
      },
    }));

    useEffect(() => {
      targetRef.current = isPaused ? 0 : 1;
    }, [isPaused]);

    useEffect(() => {
      const el = colRef.current;
      if (!el) return;

      const tick = (now) => {
        if (lastTimeRef.current == null) lastTimeRef.current = now;
        const dt = now - lastTimeRef.current;
        lastTimeRef.current = now;

        // Ease velocity toward target
        const step = dt / EASE_DURATION;
        if (velocityRef.current < targetRef.current)
          velocityRef.current = Math.min(velocityRef.current + step, 1);
        else if (velocityRef.current > targetRef.current)
          velocityRef.current = Math.max(velocityRef.current - step, 0);

        if (velocityRef.current > 0.001) {
          offsetRef.current += speed * velocityRef.current * (dt / 16.67);
        }

        // Seamless loop
        const scrollH = el.scrollHeight / 2;
        if (scrollH > 0) {
          while (offsetRef.current >= scrollH) offsetRef.current -= scrollH;
          while (offsetRef.current < 0) offsetRef.current += scrollH;
        }

        el.style.transform = `translateY(-${offsetRef.current}px)`;
        rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(rafRef.current);
    }, [speed]);

    return (
      <div className="min-w-0 flex-1 overflow-hidden">
        <div ref={colRef} className="will-change-transform">
          {[...cards, ...cards].map((article, i) => (
            <PostCard
              key={`${article.document_id}-${i}`}
              article={article}
              dark={dark}
              t={t}
              onClick={onCardClick}
              isSelected={selectedId === article.document_id}
            />
          ))}
        </div>
      </div>
    );
  },
);

/* ─── Post Wall ─── */
const PostWall = ({ dark, t }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const {
    isRightOpen,
    setIsRightOpen,
    setRightComponent,
    setRightSidebarMode,
  } = useRightSidebar();

  // Clear selection when sidebar closes
  useEffect(() => {
    if (!isRightOpen) setSelectedId(null);
  }, [isRightOpen]);

  const leftRef = useRef(null);
  const centerRef = useRef(null);
  const rightRef = useRef(null);

  const wallRef = useRef(null);

  // Wheel on the wall → nudge all columns, prevent page scroll
  useEffect(() => {
    const el = wallRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY;
      leftRef.current?.nudge(delta);
      centerRef.current?.nudge(delta);
      rightRef.current?.nudge(delta);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [loading]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getFeed(FEED_PAYLOAD);
        const data = res?.data ?? res ?? [];
        if (!cancelled) setArticles(data);
      } catch (e) {
        console.error("[PostWall] getFeed error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCardClick = useCallback(
    (article) => {
      const docId = article.document_id;

      // Toggle: clicking the same card again deselects and closes sidebar
      if (selectedId === docId) {
        setSelectedId(null);
        setIsRightOpen(false);
        return;
      }

      setSelectedId(docId);
      setRightSidebarMode(null);
      setIsRightOpen(true);
      setRightComponent(
        <ArticleDetailPanel
          article={article}
          cardId={docId}
          t={getTheme(document.documentElement.classList.contains("dark"))}
        />,
      );
    },
    [selectedId, setRightComponent, setIsRightOpen, setRightSidebarMode],
  );

  const [leftCards, centerCards, rightCards] = useMemo(() => {
    const left = [];
    const center = [];
    const right = [];
    articles.forEach((a, i) => {
      const col = i % 3;
      if (col === 0) left.push(a);
      else if (col === 1) center.push(a);
      else right.push(a);
    });
    return [left, center, right];
  }, [articles]);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <span className="text-[11px]" style={{ color: t.textMuted }}>
          Loading posts…
        </span>
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <span className="text-[11px]" style={{ color: t.textMuted }}>
          No recent posts
        </span>
      </div>
    );
  }

  const scrollPaused = isPaused || !!selectedId;

  return (
    <div className="flex h-full w-full flex-col">
      {/* Header */}
      <div
        className="shrink-0 px-3.5 py-2.5"
        style={{
          borderBottom: `1px solid ${dark ? "rgba(148,163,184,0.08)" : "rgba(203,213,225,0.3)"}`,
        }}
      >
        <span
          className="text-xs tracking-wide"
          style={{ color: t.textPrimary }}
        >
          Recent Posts
        </span>
      </div>

      {/* Scrolling wall */}
      <div
        ref={wallRef}
        className="relative flex flex-1 gap-2.5 overflow-hidden px-2.5"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Top fade */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-10 h-10"
          style={{
            background: dark
              ? "linear-gradient(to bottom, rgba(15,23,42,0.9) 0%, transparent 100%)"
              : "linear-gradient(to bottom, rgba(241,245,249,0.95) 0%, transparent 100%)",
          }}
        />
        {/* Bottom fade */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-10"
          style={{
            background: dark
              ? "linear-gradient(to top, rgba(15,23,42,0.9) 0%, transparent 100%)"
              : "linear-gradient(to top, rgba(241,245,249,0.95) 0%, transparent 100%)",
          }}
        />

        <ScrollColumn
          ref={leftRef}
          cards={leftCards}
          speed={0.45}
          dark={dark}
          t={t}
          isPaused={scrollPaused}
          onCardClick={handleCardClick}
          selectedId={selectedId}
        />
        <ScrollColumn
          ref={centerRef}
          cards={centerCards}
          speed={0.35}
          dark={dark}
          t={t}
          isPaused={scrollPaused}
          onCardClick={handleCardClick}
          selectedId={selectedId}
        />
        <ScrollColumn
          ref={rightRef}
          cards={rightCards}
          speed={0.25}
          dark={dark}
          t={t}
          isPaused={scrollPaused}
          onCardClick={handleCardClick}
          selectedId={selectedId}
        />
      </div>
    </div>
  );
};

/* ─── Suggested Topics & Questions ─── */

const ExploreSkeleton = () => (
  <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-3">
    {/* Topic chip skeletons — 2 rows */}
    <div className="mb-3 shrink-0">
      <div
        className="flex flex-wrap gap-1.5 overflow-hidden"
        style={{ maxHeight: 58 }}
      >
        {[16, 12, 14, 20, 13, 16, 12, 18, 14, 11].map((w, i) => (
          <div
            key={i}
            className="animate-pulse rounded-full bg-surfaceBorder"
            style={{ width: `${w * 4 + 20}px`, height: 24 }}
          />
        ))}
      </div>
    </div>

    <div className="mb-3 shrink-0 h-px bg-mainDivider" />

    {/* Questions label */}
    <span className="shrink-0 text-[10px] font-medium text-velBlue">
      Suggested Questions
    </span>

    {/* Question skeletons — 3 rows */}
    <div className="mt-1.5 flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="shrink-0 animate-pulse rounded-lg border border-surfaceBorder bg-surfaceBorder/30"
          style={{ height: 38 }}
        />
      ))}
    </div>
  </div>
);

const SuggestedTopics = ({ dark, t, topics, questions }) => {
  const isLoading =
    !topics || !questions || (topics.length === 0 && questions.length === 0);
  const navigate = useNavigate();

  const handleTopicClick = useCallback(
    (topic) => {
      navigate(
        `/chat?message=${encodeURIComponent(`Tell me about ${topic}...`)}`,
      );
    },
    [navigate],
  );

  const handleQuestionClick = useCallback(
    (question) => {
      navigate(`/chat?message=${encodeURIComponent(question)}`);
    },
    [navigate],
  );

  return (
    <div className="flex h-full w-full flex-col overflow-hidden font-poppins font-normal">
      {/* Header */}
      <div className="shrink-0 border-b border-mainDivider px-4 py-2.5">
        <span className="text-xs tracking-wide text-mainText">Explore</span>
      </div>

      {isLoading ? (
        <ExploreSkeleton />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-3">
          {/* Topic chips — max 2 visible rows */}
          {topics.length > 0 && (
            <div className="mb-3 shrink-0">
              <div
                className="flex flex-wrap gap-1.5 overflow-hidden"
                style={{ maxHeight: 58 }}
              >
                {topics.map((topic) => (
                  <button
                    key={topic}
                    onClick={() => handleTopicClick(topic)}
                    className="cursor-pointer rounded-full border border-inputBorder bg-transparent px-2.5 py-1 text-[10px] text-mutedText transition-all duration-150 hover:border-velBlue/30 hover:bg-velBlue/10 hover:text-velBlue active:scale-95"
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Divider */}
          {topics.length > 0 && questions.length > 0 && (
            <div className="mb-3 shrink-0 h-px bg-mainDivider" />
          )}

          {/* Questions — contained, no scroll */}
          {questions.length > 0 && (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <span className="shrink-0 text-[10px] font-medium text-velBlue">
                Suggested Questions
              </span>

              <div className="mt-1.5 flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
                {questions.slice(0, 3).map((q) => (
                  <button
                    key={q}
                    onClick={() => handleQuestionClick(q)}
                    className="flex-1 min-h-0 cursor-pointer rounded-lg border border-velBlue/15 bg-velBlue/[0.04] px-3 py-2 text-left text-[11px] leading-snug text-mainText transition-all duration-150 hover:border-velBlue/30 hover:bg-velBlue/10 active:scale-[0.99] flex items-center overflow-hidden"
                  >
                    <span className="line-clamp-3">{q}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   OverviewView
   ═══════════════════════════════════════════ */
const OverviewView = ({ dark }) => {
  const t = useMemo(() => getTheme(dark), [dark]);
  const [hasVideos, setHasVideos] = useState(true);

  // Suggestions lifted from the digest response
  const [suggestions, setSuggestions] = useState(null);

  const handleSuggestionsReady = useCallback((data) => {
    setSuggestions((prev) => {
      // Don't overwrite existing suggestions with empty data (e.g. force-refresh stream)
      const hasNew = data?.topics?.length > 0 || data?.questions?.length > 0;
      if (prev && !hasNew) return prev;
      return data;
    });
  }, []);

  return (
    <div className="flex h-full w-full gap-3 p-4 font-poppins font-normal text-mainText">
      <motion.div
        className="min-w-0 flex-1"
        custom={0}
        initial="hidden"
        animate="visible"
        variants={panelVariants}
      >
        <div className="h-full overflow-hidden rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md">
          <ImmediateDigest
            dark={dark}
            t={t}
            onSuggestionsReady={handleSuggestionsReady}
          />
        </div>
      </motion.div>

      <div className="flex flex-1 min-w-0 flex-col gap-3">
        {hasVideos && (
          <motion.div
            className="shrink-0"
            style={{ maxHeight: "33%" }}
            custom={1}
            initial="hidden"
            animate="visible"
            variants={panelVariants}
          >
            <div
              className="h-full rounded-lg overflow-hidden"
              style={{
                background: t.cardBg,
                border: `1px solid ${t.cardBorder}`,
              }}
            >
              <NewsAnchorWidget
                dark={dark}
                t={t}
                onEmpty={() => setHasVideos(false)}
              />
            </div>
          </motion.div>
        )}

        <motion.div
          className="min-h-0"
          style={{ height: "50%" }}
          custom={2}
          initial="hidden"
          animate="visible"
          variants={panelVariants}
        >
          <div className="h-full overflow-hidden rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md">
            <PostWall dark={dark} t={t} />
          </div>
        </motion.div>

        <motion.div
          className="min-h-0"
          style={{ height: "50%" }}
          custom={3}
          initial="hidden"
          animate="visible"
          variants={panelVariants}
        >
          <div className="h-full overflow-hidden rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md">
            {IS_CP ? (
              <CVEGraph dark={dark} t={t} />
            ) : (
              <SuggestedTopics
                dark={dark}
                t={t}
                topics={suggestions?.topics ?? null}
                questions={suggestions?.questions ?? null}
              />
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OverviewView;
